/**
 * Beacon Hook
 * IP-based security beacon for student lab access (Phase 7)
 *
 * Features:
 * - Auto-beacon on mount and every 5 minutes
 * - Tracks IP status (stable, campus-network, vpn-detected, pending)
 * - Provides reset function for VPN users
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { apiPost, ApiError } from '../services/api';
import type { BeaconResponse, IpStatus } from '@cloak-next/shared';

/**
 * Beacon interval (5 minutes)
 */
const BEACON_INTERVAL_MS = 5 * 60 * 1000;

export interface UseBeaconResult {
  // IP status info
  ipStatus: IpStatus | 'pending' | 'unknown';
  currentIp: string | null;
  currentCidr: string | null;
  message: string | null;

  // Actions
  sendBeacon: () => Promise<void>;
  resetIpDetection: () => Promise<void>;
  clearIp: () => Promise<void>;

  // Status
  loading: boolean;
  error: string | null;
  clearError: () => void;
  lastBeaconAt: Date | null;
}

/**
 * Hook for IP beacon functionality
 *
 * @param enabled - Whether to enable auto-beaconing (default: true)
 */
export function useBeacon(enabled: boolean = true): UseBeaconResult {
  const [ipStatus, setIpStatus] = useState<IpStatus | 'pending' | 'unknown'>('unknown');
  const [currentIp, setCurrentIp] = useState<string | null>(null);
  const [currentCidr, setCurrentCidr] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastBeaconAt, setLastBeaconAt] = useState<Date | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Send beacon to server
   */
  const sendBeacon = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiPost<{ success: boolean; data: BeaconResponse }>(
        '/labs/beacon'
      );

      if (!mountedRef.current) return;

      const { data } = response;
      setIpStatus(data.ipStatus);
      setCurrentIp(data.currentIp);
      setCurrentCidr(data.currentCidr);
      setMessage(data.message || null);
      setLastBeaconAt(new Date());

      console.log('[Beacon] Status:', data.ipStatus, 'IP:', data.currentIp, 'CIDR:', data.currentCidr);
    } catch (err) {
      if (!mountedRef.current) return;

      // Don't show error for 'no instances' case - this is expected for new students
      if (err instanceof ApiError && err.statusCode === 400) {
        setIpStatus('pending');
        setMessage('Waiting for lab instance assignment...');
        return;
      }

      const errorMessage = err instanceof ApiError ? err.message : 'Failed to send beacon';
      setError(errorMessage);
      console.error('[Beacon] Failed:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Reset IP detection
   */
  const resetIpDetection = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      await apiPost('/labs/reset-ip');

      if (!mountedRef.current) return;

      // Clear local state
      setIpStatus('unknown');
      setCurrentIp(null);
      setCurrentCidr(null);
      setMessage('IP detection reset. Sending new beacon...');

      // Send fresh beacon after reset
      setTimeout(() => {
        if (mountedRef.current) {
          sendBeacon();
        }
      }, 1000);
    } catch (err) {
      if (!mountedRef.current) return;

      const errorMessage = err instanceof ApiError ? err.message : 'Failed to reset IP detection';
      setError(errorMessage);
      console.error('[Beacon] Reset failed:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [sendBeacon]);

  /**
   * Clear current IP rule without auto-beaconing (manual override)
   */
  const clearIp = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      await apiPost('/labs/reset-ip');

      if (!mountedRef.current) return;

      setIpStatus('pending');
      setCurrentIp(null);
      setCurrentCidr(null);
      setMessage(null);
    } catch (err) {
      if (!mountedRef.current) return;

      const errorMessage = err instanceof ApiError ? err.message : 'Failed to clear IP';
      setError(errorMessage);
      console.error('[Beacon] Clear failed:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Auto-beacon on mount and interval
  useEffect(() => {
    if (!enabled) return;

    mountedRef.current = true;

    // Send initial beacon after a short delay
    const initialTimeout = setTimeout(() => {
      if (mountedRef.current) {
        sendBeacon();
      }
    }, 1000);

    // Set up interval for periodic beacons
    intervalRef.current = setInterval(() => {
      if (mountedRef.current) {
        sendBeacon();
      }
    }, BEACON_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, sendBeacon]);

  return {
    // IP status info
    ipStatus,
    currentIp,
    currentCidr,
    message,

    // Actions
    sendBeacon,
    resetIpDetection,
    clearIp,

    // Status
    loading,
    error,
    clearError,
    lastBeaconAt,
  };
}
